/**
 * Inters (Platform Alignment) component that fetches its own data.
 * Uses per-section API endpoints for better performance.
 */
import type {
  FieldProvenanceDto,
  MissionAssessmentInterSelectDto,
  MissionAssessmentInterType,
  VerificationConfidence,
} from '@@types/api.types';
import { BadgeList } from '@components/shared/badge-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { Score } from '@components/shared/score.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentIntersQueryOptions,
} from '@queries/mission-assessments.query';
import { useQuery } from '@tanstack/react-query';
import { FIELDED_STATUS_LABELS, VERIFICATION_CONFIDENCE_LABELS, countries } from '@utils/constants';
import type { FunctionComponent } from 'react';
import { TooltipComponent } from '../../../core/tooltip.component';
import { MissionAssessmentIntersSkeleton } from './mission-assessment-skeletons.component';
import { TechnologyPlatformAlignmentSankey } from './technology-platform-alignment-sankey.component';

type TechnologyMissionAssessmentIntersProps = {
  missionAssessmentId: string;
};

type InterRow = {
  category: string;
  categoryDescription?: string;
  country?: string;
  description?: string;
  digitalIntegrations?: Array<{ id: string; name: string }>;
  fieldedStatus?: string;
  mrl?: number;
  name?: string;
  notes: string;
  operationalIntegrations?: Array<{ id: string; name: string }>;
  physicalIntegrations?: Array<{ id: string; name: string }>;
  priorityRationale?: string;
  priorityScore?: number;
  reference?: string;
  subcategory?: string;
  trl?: number;
  verificationConfidence?: VerificationConfidence;
  verificationNotes?: string;
};

const getPriorityScoreBadgeVariant = (score: number): 'destructive' | 'success' | 'warning' => {
  if (score >= 7) return 'success';
  if (score >= 4) return 'warning';
  return 'destructive';
};

const getVerificationConfidenceBadgeVariant = (
  confidence: VerificationConfidence,
): 'destructive' | 'success' | 'warning' => {
  if (confidence === 'HIGH') return 'success';
  if (confidence === 'MEDIUM') return 'warning';
  return 'destructive';
};

const getCountryName = (countryCode?: string): string | undefined => {
  if (!countryCode) return undefined;
  const country = countries.find((c) => c.code === countryCode);
  return country?.name;
};

const mapInterToRow = (inter: MissionAssessmentInterSelectDto): InterRow => ({
  category: inter.category.name,
  categoryDescription: inter.category.description,
  country: inter.country,
  description: inter.description,
  digitalIntegrations: inter.digitalIntegrations?.map((di) => ({ id: di.id, name: di.name })),
  fieldedStatus: inter.fieldedStatus,
  mrl: inter.mrl,
  name: inter.name,
  notes: inter.notes,
  operationalIntegrations: inter.operationalIntegrations?.map((oi) => ({ id: oi.id, name: oi.name })),
  physicalIntegrations: inter.physicalIntegrations?.map((pi) => ({ id: pi.id, name: pi.name })),
  priorityRationale: inter.priorityRationale,
  priorityScore: inter.priorityScore,
  reference: inter.reference,
  subcategory: inter.subcategory?.name,
  trl: inter.trl,
  verificationConfidence: inter.verificationConfidence,
  verificationNotes: inter.verificationNotes,
});

const InterRowAccordion: FunctionComponent<{
  provenance?: FieldProvenanceDto | null;
  row: InterRow;
  rowIndex: number;
}> = (props) => {
  const { provenance, row, rowIndex } = props;

  return (
    <Accordion
      className="border rounded-lg bg-white border-gray-200"
      key={`${row.name || row.category}-${rowIndex}`}
      type="multiple"
    >
      <AccordionItem
        className="border-none"
        value="item"
      >
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center w-full">
            <div className="flex flex-col items-start gap-1 text-left">
              <div className="flex items-center gap-2">
                <h4 className="text-sm text-gray-900">{row.name ?? 'Unknown name'}</h4>
                <FieldProvenanceBadge provenance={provenance} />
              </div>
              {row.reference && <p className="text-sm text-gray-500">{row.reference}</p>}
            </div>

            {Boolean(row.trl || row.mrl) && (
              <div className="flex flex-wrap ml-auto pr-8 items-center font-normal text-left gap-4">
                {row.trl !== undefined && row.trl !== null && (
                  <Score
                    score={row.trl}
                    size="small"
                    title="TRL"
                    subtitle="Score"
                    type="TRL"
                  />
                )}
                {row.mrl !== undefined && row.mrl !== null && (
                  <Score
                    score={row.mrl}
                    size="small"
                    title="MRL"
                    subtitle="Score"
                    type="MRL"
                  />
                )}
              </div>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="space-y-6">
            {row.description && (
              <div>
                <BlueLabel text="Description" />
                <ExpandableText
                  className="text-gray-600 text-sm leading-relaxed"
                  maxLines={3}
                  text={row.description}
                />
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <BlueLabel text="Category" />
                <div className="flex items-center gap-1">
                  <p className="text-gray-600 text-sm">{row.category}</p>
                  {row.categoryDescription && (
                    <TooltipComponent
                      className="text-xs whitespace-pre-wrap font-normal"
                      side="right"
                      triggerClassName="text-gray-500 bg-white"
                    >
                      {row.categoryDescription}
                    </TooltipComponent>
                  )}
                </div>
              </div>
              {row.subcategory && (
                <div>
                  <BlueLabel text="Subcategory" />
                  <p className="text-gray-600 text-sm">{row.subcategory}</p>
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {row.fieldedStatus && (
                <div>
                  <BlueLabel text="Fielded Status" />
                  <p className="text-gray-600 text-sm">{FIELDED_STATUS_LABELS[row.fieldedStatus]}</p>
                </div>
              )}

              {row.country && getCountryName(row.country) && (
                <div>
                  <BlueLabel text="Country" />
                  <p className="text-gray-600 text-sm">{getCountryName(row.country)}</p>
                </div>
              )}
            </div>

            {row.notes && (
              <div>
                <BlueLabel text="Notes" />
                <ExpandableText
                  className="text-gray-600 text-sm leading-relaxed"
                  maxLines={3}
                  text={row.notes}
                />
              </div>
            )}

            {(row.priorityScore !== undefined || row.priorityRationale) && (
              <>
                <hr className="border-gray-200" />
                <div className="space-y-3">
                  {row.priorityScore !== undefined && row.priorityScore !== null && (
                    <div className="flex flex-col items-start gap-y-2">
                      <BlueLabel text="Priority Score" />
                      <Badge variant={getPriorityScoreBadgeVariant(row.priorityScore)}>{row.priorityScore}</Badge>
                    </div>
                  )}

                  {row.priorityRationale && (
                    <div>
                      <BlueLabel text="Priority Rationale" />
                      <ExpandableText
                        className="text-gray-600 text-sm leading-relaxed"
                        maxLines={3}
                        text={row.priorityRationale}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {(row.verificationConfidence || row.verificationNotes) && (
              <>
                <hr className="border-gray-200" />
                <div className="space-y-3">
                  {row.verificationConfidence && (
                    <div className="flex flex-col items-start gap-y-2">
                      <BlueLabel text="Verification Confidence" />
                      <Badge variant={getVerificationConfidenceBadgeVariant(row.verificationConfidence)}>
                        {VERIFICATION_CONFIDENCE_LABELS[row.verificationConfidence]}
                      </Badge>
                    </div>
                  )}

                  {row.verificationNotes && (
                    <div>
                      <BlueLabel text="Verification Notes" />
                      <ExpandableText
                        className="text-gray-600 text-sm leading-relaxed"
                        maxLines={3}
                        text={row.verificationNotes}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {((row.digitalIntegrations && row.digitalIntegrations.length > 0) ||
              (row.operationalIntegrations && row.operationalIntegrations.length > 0) ||
              (row.physicalIntegrations && row.physicalIntegrations.length > 0)) && (
              <>
                <hr className="border-gray-200" />
                <div className="space-y-4">
                  {row.digitalIntegrations && row.digitalIntegrations.length > 0 && (
                    <div className="flex flex-col gap-y-2">
                      <BlueLabel text="Digital Integrations" />
                      <BadgeList
                        badgeVariant="outline"
                        items={row.digitalIntegrations}
                        limit={5}
                        size="sm"
                      />
                    </div>
                  )}

                  {row.operationalIntegrations && row.operationalIntegrations.length > 0 && (
                    <div className="flex flex-col gap-y-2">
                      <BlueLabel text="Operational Integrations" />
                      <BadgeList
                        badgeVariant="outline"
                        items={row.operationalIntegrations}
                        limit={5}
                        size="sm"
                      />
                    </div>
                  )}

                  {row.physicalIntegrations && row.physicalIntegrations.length > 0 && (
                    <div className="flex flex-col gap-y-2">
                      <BlueLabel text="Physical Integrations" />
                      <BadgeList
                        badgeVariant="outline"
                        items={row.physicalIntegrations}
                        limit={5}
                        size="sm"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export const TechnologyMissionAssessmentIntersComponent: FunctionComponent<TechnologyMissionAssessmentIntersProps> = (
  props,
) => {
  const { missionAssessmentId } = props;

  const { data: coreData, isLoading: isCoreLoading } = useQuery(
    useMissionAssessmentCoreQueryOptions(missionAssessmentId),
  );
  const { data: intersData, isLoading: isIntersLoading } = useQuery(
    useMissionAssessmentIntersQueryOptions(missionAssessmentId),
  );

  const isLoading = isCoreLoading || isIntersLoading;

  if (isLoading) {
    return <MissionAssessmentIntersSkeleton />;
  }

  const inters = intersData?.inters ?? [];
  const technology = coreData?.technology;

  const hasInterchangeableData = inters.some(
    (inter) => inter.type === ('INTERCHANGEABLE' as MissionAssessmentInterType),
  );
  const hasInteroperableData = inters.some((inter) => inter.type === ('INTEROPERABLE' as MissionAssessmentInterType));
  const shouldShowSankey = hasInterchangeableData && hasInteroperableData;

  const sections = [
    {
      provenance: coreData?.provenance?.INTERCHANGEABLE,
      rows: inters
        .filter((inter) => inter.type === ('INTERCHANGEABLE' as MissionAssessmentInterType))
        .map(mapInterToRow),
      title: 'Interchangeable with',
    },
    {
      provenance: coreData?.provenance?.INTEROPERABLE,
      rows: inters.filter((inter) => inter.type === ('INTEROPERABLE' as MissionAssessmentInterType)).map(mapInterToRow),
      title: 'Interoperable with',
    },
  ];

  return (
    <div className="flex flex-col gap-y-4">
      <section>
        {shouldShowSankey && technology && (
          <TechnologyPlatformAlignmentSankey
            inters={inters}
            sankeyProps={{ maxHeight: 500 }}
            technology={technology}
          />
        )}
      </section>

      {sections.map((section) => (
        <div
          className="flex flex-col"
          key={section.title}
        >
          <h3 className="text-gray-600 font-medium text-sm mb-2">{section.title}</h3>
          <div className="flex flex-col gap-y-3">
            {section.rows.length > 0 ? (
              section.rows.map((row, rowIndex) => (
                <InterRowAccordion
                  key={`${row.name || row.category}-${rowIndex}`}
                  provenance={section.provenance}
                  row={row}
                  rowIndex={rowIndex}
                />
              ))
            ) : (
              <div className="text-gray-500 text-xs font-base">No data available</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
